import { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FactoryIcon from "@mui/icons-material/Factory";
import SchoolIcon from "@mui/icons-material/School";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsIcon from "@mui/icons-material/Settings";
import axiosInstance from "../../../axios/axiosInstance";
import { workSetupOptions } from "../../career_journey/utils/utils";

const formatSalary = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Math.round(Number(value));
  if (isNaN(num)) return value;
  if (num <= 9999) return num.toString();
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "’");
};

function CareerJourney() {
  const [careerJourneyData, setCareerJourneyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Fetch the schedule and then fetch the career journey for the first one
  useEffect(() => {
    const fetchFirstCareerJourney = async () => {
      try {
        const scheduleRes = await axiosInstance.get(
          `/api/profile/candidate-eq-schedule`,
        );

        if (scheduleRes.data?.length > 0) {
          const firstId = scheduleRes.data[0]?.user_id;
          if (firstId) {
            await fetchCareerJourneyData(firstId);
          } else {
            setError("No valid user ID found.");
          }
        } else {
          setError("No scheduled interviews found.");
        }
      } catch (err) {
        console.error("Error fetching schedule:", err);
        setError("Failed to fetch schedule data.");
      } finally {
        setLoading(false);
      }
    };

    const fetchCareerJourneyData = async (careerJourneyId) => {
      try {
        const res = await axiosInstance.get(
          `/api/admin/career-journey/full/${careerJourneyId}`,
        );
        const data = res.data;

        const commonData = {
          ep: data?.common?.ep || "EP not provided",
          overview: data?.common?.overview || "",
          mainAchievements: data?.common?.mainAchievements || "",
          professional: data?.common?.professional || "",
          personal: data?.common?.personal || "",
          why: data?.common?.why || "",
          preferredEnvironment: data?.common?.preferredEnvironment || [],
          preferredWorkEnvironment:
            data?.common?.preferredWorkEnvironment || [],
          motivatedBy: data?.common?.motivatedBy || "",
          developmentPotential: data?.common?.developmentPotential || "",
          languages:
            data?.common?.languages?.length > 0
              ? data.common.languages.map((l) => `${l.name} (${l.fluency})`)
              : [],
          educations: data?.common?.educations || [],
          workSetup: data?.common?.workSetup || [],
          certifications: data?.common?.certifications || [],
          experienceLevel: data?.common?.experienceLevel || "",
          overallExperience: data?.common?.overallExperience || {},
          relevantExperience: data?.common?.relevantExperience || [],
          salaryExpectation: data?.common?.salaryExpectation || "",
          salaryCurrency: data?.common?.salaryCurrency || "USD",
          salaryPeriod: data?.common?.salaryPeriod || "year",
          location: data?.common?.location || [],
          noticePeriod: data?.common?.noticePeriod || {},
          noticePeriodDays: data?.common?.noticePeriodDays,
          erpExperience: data?.common?.erpExperience || [],
          additionalSystems: data?.common?.additionalSystems || [],
          peerReviewed: data?.common?.peerReview || false,
        };

        const roles =
          data?.roles?.map((role) => ({
            roleTitle: role?.roleTitle || role?.roleName || "",
            overallExperience:
              role?.overallExperience !== null &&
                role?.overallExperience !== undefined
                ? role.overallExperience
                : commonData.overallExperience,
            relevantExperience:
              role?.relevantExperience !== null &&
                role?.relevantExperience !== undefined
                ? role.relevantExperience
                : commonData.relevantExperience,
            salaryExpectation:
              role?.salaryExpectation !== null &&
                role?.salaryExpectation !== undefined
                ? role.salaryExpectation
                : commonData.salaryExpectation,
            salaryCurrency:
              role?.salaryCurrency !== null &&
                role?.salaryCurrency !== undefined
                ? role.salaryCurrency
                : commonData.salaryCurrency,
            salaryPeriod:
              role?.salaryPeriod !== null && role?.salaryPeriod !== undefined
                ? role.salaryPeriod
                : commonData.salaryPeriod,
            location:
              role?.location !== null && role?.location !== undefined
                ? role.location
                : commonData.location,
            noticePeriod:
              role?.noticePeriod !== null && role?.noticePeriod !== undefined
                ? role.noticePeriod
                : commonData.noticePeriod,
            noticePeriodDays:
              role?.noticePeriodDays !== null &&
                role?.noticePeriodDays !== undefined
                ? role.noticePeriodDays
                : commonData.noticePeriodDays,
            preferredIndustry: role?.preferredIndustry || "",
            erpExperience:
              role?.erpExperience !== null && role?.erpExperience !== undefined
                ? role.erpExperience
                : commonData.erpExperience,
            additionalSystems:
              role?.additionalSystems !== null &&
                role?.additionalSystems !== undefined
                ? role.additionalSystems
                : commonData.additionalSystems,
          })) || [];

        setCareerJourneyData({ ...commonData, roles });
      } catch (err) {
        console.error("Error fetching career journey:", err);
        setError("Career journey not available.");
      }
    };

    fetchFirstCareerJourney();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (!careerJourneyData) {
    return (
      <Typography align="center" sx={{ color: "#666" }}>
        No career journey data found.
      </Typography>
    );
  }

  // ✅ Inline Career Journey display (no dialog)
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        width: "100%",
        mx: "auto",
        mt: 0,
        backgroundColor: "#ffffff",
        borderRadius: 2,
        border: "1px solid #e5e7eb",
      }}
    >
      {/* Career Journey Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#111827",
              fontSize: "1.5rem",
            }}
          >
            Career Journey (CJ)
          </Typography>

          {careerJourneyData.peerReviewed && (
            <Tooltip title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility.">
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                label="Hiring Manager Evaluated"
                size="small"
                color="primary"
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: "#111827",
            fontWeight: 500,
            mb: 2,
            fontSize: "1.25rem",
          }}
        >
          {careerJourneyData.ep}
        </Typography>
        <Typography
          sx={{
            color: "#374151",
            lineHeight: 1.7,
            mb: 3,
            fontSize: "0.95rem",
          }}
        >
          {careerJourneyData.overview}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Overall Experience */}
      {careerJourneyData.overallExperience?.value > 0 &&
        careerJourneyData.overallExperience?.unit && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                mb: 1,
                fontSize: "1rem",
              }}
            >
              Overall experience:
            </Typography>
            <Typography sx={{ color: "#374151", ml: 2 }}>
              {careerJourneyData.overallExperience.value}{" "}
              {careerJourneyData.overallExperience.unit}
            </Typography>
          </Box>
        )}

      {/* Most Relevant Experiences */}
      {careerJourneyData.relevantExperience?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Most relevant experiences
          </Typography>
          <List sx={{ pl: 0, listStyle: "disc", ml: 3 }}>
            {careerJourneyData.relevantExperience.map((exp, i) => (
              <ListItem
                key={i}
                sx={{
                  display: "list-item",
                  pl: 0,
                  py: 0.5,
                  color: "#374151",
                }}
              >
                <Typography component="span">
                  {exp.role}, {exp.company}
                  {exp.duration && ` (${exp.duration})`}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Main Achievements */}
      {careerJourneyData.mainAchievements && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Main Achievements:
          </Typography>
          <Typography sx={{ color: "#374151", ml: 2 }}>
            {careerJourneyData.mainAchievements}
          </Typography>
        </Box>
      )}

      {/* Value Add */}
      {(careerJourneyData.professional || careerJourneyData.personal) && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Value add:
          </Typography>
          {careerJourneyData.professional && (
            <Box sx={{ ml: 2, mb: 1 }}>
              <Typography
                component="span"
                sx={{ fontWeight: 500, color: "#111827" }}
              >
                Professional:
              </Typography>
              <Typography component="span" sx={{ color: "#374151", ml: 1 }}>
                {careerJourneyData.professional}
              </Typography>
            </Box>
          )}
          {careerJourneyData.personal && (
            <Box sx={{ ml: 2 }}>
              <Typography
                component="span"
                sx={{ fontWeight: 500, color: "#111827" }}
              >
                Personal:
              </Typography>
              <Typography component="span" sx={{ color: "#374151", ml: 1 }}>
                {careerJourneyData.personal}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* The WHY */}
      {careerJourneyData.why && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            The WHY:
          </Typography>
          <Typography sx={{ color: "#374151", ml: 2 }}>
            {careerJourneyData.why}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Key Details with Icons */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 600,
            color: "#111827",
            mb: 2,
            fontSize: "1rem",
          }}
        >
          Key details:
        </Typography>
        <Grid container spacing={2}>
          {careerJourneyData.salaryExpectation && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AttachMoneyIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      fontWeight: 500,
                    }}
                  >
                    Salary expectations:
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "0.9rem" }}>
                    {formatSalary(careerJourneyData.salaryExpectation)}{" "}
                    {careerJourneyData.salaryCurrency}{" "}
                    {careerJourneyData.salaryPeriod &&
                      (careerJourneyData.salaryPeriod.startsWith("per")
                        ? careerJourneyData.salaryPeriod
                        : `per ${careerJourneyData.salaryPeriod}`)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {careerJourneyData?.workSetup &&
            careerJourneyData.workSetup.length > 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BusinessIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      Work model:
                    </Typography>
                    <Typography sx={{ color: "#111827", fontSize: "0.9rem" }}>
                      {typeof careerJourneyData.workSetup[0] === "string"
                        ? careerJourneyData.workSetup.join(", ")
                        : workSetupOptions
                          .filter((opt) =>
                            careerJourneyData.workSetup.includes(opt.id),
                          )
                          .map((opt) => opt.name)
                          .join(", ")}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

          {careerJourneyData?.languages?.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LanguageIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      fontWeight: 500,
                    }}
                  >
                    Languages:
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "0.9rem" }}>
                    {careerJourneyData.languages.join(", ")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {careerJourneyData.location?.length > 0 && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      fontWeight: 500,
                    }}
                  >
                    Location:
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "0.9rem" }}>
                    {careerJourneyData.location
                      .map((loc) => `${loc.cities.join(", ")} (${loc.country})`)
                      .join(" | ")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {careerJourneyData.noticePeriod && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      fontWeight: 500,
                    }}
                  >
                    Notice period:
                  </Typography>
                  <Typography sx={{ color: "#111827", fontSize: "0.9rem" }}>
                    {careerJourneyData.noticePeriod.value === 0
                      ? "Available Immediately"
                      : `${careerJourneyData.noticePeriod.value} ${careerJourneyData.noticePeriod.value === 1
                        ? careerJourneyData.noticePeriod.unit.replace(
                          /s$/,
                          "",
                        )
                        : careerJourneyData.noticePeriod.unit
                      }`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Preferred Environment */}
      {careerJourneyData?.preferredEnvironment?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Preferred Company Type:
          </Typography>
          <List sx={{ pl: 0, listStyle: "disc", ml: 3 }}>
            {careerJourneyData.preferredEnvironment.map((env, i) => (
              <ListItem
                key={i}
                sx={{
                  display: "list-item",
                  pl: 0,
                  py: 0.5,
                  color: "#374151",
                }}
              >
                <Typography component="span">{env}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {careerJourneyData?.preferredWorkEnvironment?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Preferred Work Environment:
          </Typography>
          <List sx={{ pl: 0, listStyle: "disc", ml: 3 }}>
            {careerJourneyData?.preferredWorkEnvironment.map((env, i) => (
              <ListItem
                key={i}
                sx={{
                  display: "list-item",
                  pl: 0,
                  py: 0.5,
                  color: "#374151",
                }}
              >
                <Typography component="span">{env}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Development potential */}
      {careerJourneyData?.developmentPotential && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Areas of Improvements:
          </Typography>
          <Typography sx={{ color: "#374151", ml: 2 }}>
            {careerJourneyData.developmentPotential}
          </Typography>
        </Box>
      )}

      {/* ERP Experience */}
      {/* {careerJourneyData.erpExperience?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            ERP experience:
          </Typography>
          <List sx={{ pl: 0, listStyle: "disc", ml: 3 }}>
            {careerJourneyData.erpExperience.map((erp, i) => (
              <ListItem
                key={i}
                sx={{
                  display: "list-item",
                  pl: 0,
                  py: 0.5,
                  color: "#374151",
                }}
              >
                <Typography component="span">
                  {erp.system} ({erp.level})
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )} */}

      {/* Additional Systems */}
      {careerJourneyData.additionalSystems?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#111827",
              mb: 1,
              fontSize: "1rem",
            }}
          >
            Additional systems:
          </Typography>
          <List sx={{ pl: 0, listStyle: "disc", ml: 3 }}>
            {careerJourneyData.additionalSystems.map((sys, i) => (
              <ListItem
                key={i}
                sx={{
                  display: "list-item",
                  pl: 0,
                  py: 0.5,
                  color: "#374151",
                }}
              >
                <Typography component="span">
                  {sys.system} ({sys.level})
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Education */}
      {careerJourneyData.educations?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <SchoolIcon sx={{ color: "#111827", fontSize: 24 }} />
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Education
            </Typography>
          </Box>
          {careerJourneyData.educations.map((edu, idx) => (
            <Card
              key={idx}
              sx={{
                mb: 2,
                boxShadow: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 1,
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111827", mb: 0.5 }}
                >
                  {edu.degree}
                </Typography>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {edu.institution_name}
                </Typography>
                {edu.field_of_study && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    {edu.field_of_study}
                  </Typography>
                )}
                {edu.location && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    {edu.location}
                  </Typography>
                )}
                {edu.program && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    {edu.program}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Certifications */}
      {careerJourneyData.certifications?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WorkspacePremiumIcon sx={{ color: "#111827", fontSize: 24 }} />
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: "1rem",
              }}
            >
              Certifications
            </Typography>
          </Box>
          {careerJourneyData.certifications.map((cert, idx) => (
            <Card
              key={idx}
              sx={{
                mb: 2,
                boxShadow: 0,
                border: "1px solid #e5e7eb",
                borderRadius: 1,
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111827", mb: 0.5 }}
                >
                  {cert.certification_name}
                </Typography>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {cert.issuing_organization}
                </Typography>
                {cert.credential_url && (
                  <Link
                    href={cert.credential_url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: "0.875rem", mt: 0.5, display: "block" }}
                  >
                    View Credential
                  </Link>
                )}
                {cert.credential_id && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", mt: 0.5 }}
                  >
                    ID: {cert.credential_id}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default CareerJourney;
